// Dev-only stub data for visual testing of the Flow chat UI.
// Set STUB_MODE = true to use these stubs instead of making real API calls.
// Remember to set it back to false before committing.

export const STUB_MODE = false;

export const STUB_TOPIC_ID = '00000000-0000-0000-0000-000000000001';

function ago(minutes: number): string {
    return new Date(Date.now() - minutes * 60 * 1000).toISOString();
}

export const STUB_TOPICS: any[] = [
    {
        id: STUB_TOPIC_ID,
        title: 'Sprint Planning Discussion',
        user_id: 1,
        project_id: 1,
        is_shared: false,
        is_pinned: false,
        meta: {},
        create_date: ago(120),
    },
    {
        id: '00000000-0000-0000-0000-000000000002',
        title: 'Wiki Search Regression',
        user_id: 1,
        project_id: 1,
        is_shared: false,
        is_pinned: false,
        meta: {},
        create_date: ago(60 * 24 * 3),
    },
];

export const STUB_MESSAGES: any[] = [
    {
        id: 1,
        topic_id: STUB_TOPIC_ID,
        user_id: 1,
        role: 'user',
        content: 'Can you summarize what we should focus on in the next sprint?',
        meta: {},
        create_date: ago(110),
    },
    {
        id: 2,
        topic_id: STUB_TOPIC_ID,
        user_id: 0,
        role: 'assistant',
        content: "Based on your current backlog, I'd recommend prioritizing the authentication improvements and the tracker performance fixes. Both have been flagged as high priority and have clear acceptance criteria.",
        meta: {},
        create_date: ago(109),
    },
    {
        id: 3,
        topic_id: STUB_TOPIC_ID,
        user_id: 1,
        role: 'user',
        content: "Here are the items I was thinking about:\n\n- **Tracker performance** — query times over 2s\n- **Auth token refresh** — currently broken on mobile\n- The wiki search regression\n\nDoes that align with what you see?",
        meta: {},
        create_date: ago(105),
    },
    {
        id: 4,
        topic_id: STUB_TOPIC_ID,
        user_id: 0,
        role: 'assistant',
        content: `Yes, that aligns well. Here's a more detailed breakdown:

## High Priority

- **Tracker performance**: The root cause is likely a missing index on \`tracker_item.assigned_to\` combined with an N+1 query in \`TrackerItemService::getItems()\`. Resolving this should bring query time under 200ms.
- **Auth token refresh**: The mobile client is not sending the \`GFAuthToken\` header on refresh requests. This is a one-line fix in the interceptor, but requires regression testing across all auth flows.

## Medium Priority

- **Wiki search regression**: Introduced in the last merge. The full-text search index is no longer being updated on page save — likely a missing call to \`WikiSearchService::reindex()\` in the save handler.

## Deferred

- Dashboard widget reorder (no clear owner)
- Forum pagination UX (low complaint volume)
- SCM webhook retry logic (workaround in place)

## Estimates

| Item | Estimate | Owner |
|---|---|---|
| Tracker performance | 3 points | Backend team |
| Auth token refresh | 1 point | Frontend team |
| Wiki search | 2 points | Fullstack |

Total: **6 points** — fits comfortably in a two-week sprint alongside routine maintenance.`,
        meta: {},
        create_date: ago(104),
    },
    {
        id: 5,
        topic_id: STUB_TOPIC_ID,
        user_id: 1,
        role: 'user',
        content: 'Show me what the tracker query fix might look like in code.',
        meta: {},
        create_date: ago(90),
    },
    {
        id: 6,
        topic_id: STUB_TOPIC_ID,
        user_id: 0,
        role: 'assistant',
        content: `Here's the likely fix in \`TrackerItemService.php\`:

\`\`\`php
// Before: N+1 — fetches assignee for each item separately
public function getItems(int $trackerId, array $filters): array {
    $items = $this->TrackerItemRepository->findByTracker($trackerId, $filters);
    foreach ($items as $item) {
        $item->assignee = $this->UserRepository->findById($item->assigned_to);
    }
    return $items;
}

// After: single JOIN query
public function getItems(int $trackerId, array $filters): array {
    return $this->TrackerItemRepository->findByTrackerWithAssignees($trackerId, $filters);
}
\`\`\`

And in \`TrackerItemRepository.php\`, add the new method:

\`\`\`php
public function findByTrackerWithAssignees(int $trackerId, array $filters): array {
    $qb = $this->em->createQueryBuilder();
    $qb->select('i', 'u')
       ->from(TrackerItem::class, 'i')
       ->leftJoin('i.assignee', 'u')
       ->where('i.tracker = :trackerId')
       ->setParameter('trackerId', $trackerId);
    // ... apply $filters
    return $qb->getQuery()->getResult();
}
\`\`\`

You'll also want to add a DB index:

\`\`\`sql
CREATE INDEX CONCURRENTLY idx_tracker_item_assigned_to
    ON tracker_item (assigned_to)
    WHERE assigned_to IS NOT NULL;
\`\`\``,
        meta: {},
        create_date: ago(89),
    },
    {
        id: 7,
        topic_id: STUB_TOPIC_ID,
        user_id: 1,
        role: 'user',
        content: 'Good. What about sprint capacity?',
        meta: {},
        create_date: ago(60),
    },
    {
        id: 8,
        topic_id: STUB_TOPIC_ID,
        user_id: 0,
        role: 'assistant',
        content: `Based on your team's historical velocity, here's the capacity picture:

| Team member | Availability | Capacity |
|---|---|---|
| Alice | 100% | 8 points |
| Bob | 80% (PTO Wed–Fri) | 6 points |
| Carol | 100% | 8 points |
| Dave | 60% (on-call rotation) | 5 points |

**Total capacity: 27 points.** With the 6-point sprint goal, you have substantial buffer for unknowns or to pull in a lower-priority item. I'd suggest leaving ~8 points unallocated as a hedge given the auth token work has broad test surface area.`,
        meta: {},
        create_date: ago(59),
    },
];
